/**
 * 設定 → 振込口座（043、docs/32、#79 → #155、docs/64 §2、Figma「振込口座（10/9）」⑪⑫⑯）。管理者だけ。iOS のまとまり（docs/10 §9.15）。
 * docs/32 の決めごと（既定は会社に1つ・コードで名前が自動・出した請求書は固める など）・DB・API は変えていない。変えたのは画面だけ
 *   ・上の帯「‹ 設定｜追加」（PC の2列は「‹ 設定」を出さず、題の右に「追加」）、大きな題「振込口座」
 *   ・口座の行（BankAccountRow）：銀行名 支店名（既定は太字＋✓既定）・種別 口座番号　名義・›。行を押すと修正のシート。
 *     行ごとの「既定にする」「修正」のボタンは無い（★2 a：既定はシートの「既定の口座」のスイッチ）
 *   ・注1行・節「紙の印字（既定の口座）」（その1行。見出しの「？」に暗号化などの説明）
 *   ・まだ無いとき（⑫）：カード（口座の絵・「振込口座はまだありません」・「空のままでも請求書は出せます」・「口座追加」）
 *   ・以前の振込先（024 の1行が残っている会社）：節「以前の振込先」＝その1行と「この内容で登録 ›」（欄に分けた追加のシート）
 *   ・口座を追加・修正はシート（★1 a。SettingsBankAccountEdit.tsx の BankAccountSheet）。URL は今のまま＝/settings/bank-accounts/new・/:id で
 *     この画面の上にシートが開く（app.tsx は3つの URL ともこの画面。古いリンク・請求書の画面のリンクもそのまま効く）。
 *     開く・閉じる（取消・Esc・後ろ・保存・削除）は replace＝履歴を増やさない（「‹ 設定」の行き先は開く前のまま）
 *   ・PC の2列の右は最大 600px（Figma ⑯。表をやめたので幅いっぱいにしない）
 *   ・担当者は今どおり：設定のメニューに出ない。直接開いても「管理者だけ」の1行
 *   請求書は起こした時点の既定の口座を印字し、下書きのあいだ選び直せる（Invoice.tsx）
 */
import { BANK_MSG, BANK_TYPE_LABEL, bankAccountText, type BankAccountRow as BankAccount, type BankAccountsPage } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, type ReactNode } from "react";
import { Link, useLocation, useMatch, useNavigate, useParams } from "react-router";
import { api, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { BankAccountRow, GroupList, GroupNavRow, GroupSection, GroupTextRow, RowIcon } from "../components/GroupList.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { useSettingsPane } from "../lib/settingsPane.js";
import { BankAccountSheet, type BankAccountsSaved } from "./SettingsBankAccountEdit.jsx";

export const BANK_ACCOUNTS_KEY = ["bank-accounts"] as const;
const LIST = "/settings/bank-accounts";

/** 2行目：種別 口座番号　名義 */
const subOf = (a: BankAccount) => `${BANK_TYPE_LABEL[a.accountType]} ${a.accountNo}　${a.holder}`;

/** 開いたシート（履歴の key ごとに中身を作り直す。閉じる動きのあいだも描く） */
type SheetFor = { key: string; id: string; row: BankAccount | undefined; legacy: string | undefined };

export function SettingsBankAccountsPage() {
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ 設定（シートの開け閉めは replace なので変わらない）
  const back = useBackNav({ to: "/settings", label: "設定" });
  const me = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  const pane = useSettingsPane();
  const { id } = useParams();
  const isNew = !!useMatch(`${LIST}/new`);
  /** URL が開けと言っているシート（"new"＝追加、uuid＝修正） */
  const want = isNew ? "new" : (id ?? null);
  const { toast, node } = useToast();
  const admin = me.data?.user.role === "admin";
  const q = useQuery({ queryKey: BANK_ACCOUNTS_KEY, queryFn: () => api<BankAccountsPage>("GET", "/bank-accounts"), enabled: admin });
  const target = want && want !== "new" ? q.data?.accounts.find((a) => a.id === want) : undefined;
  const open = admin && !!q.data && (want === "new" || !!target);
  usePageName(open ? (want === "new" ? BANK_MSG.addTitle : BANK_MSG.editTitle) : BANK_MSG.title);
  const last = useRef<SheetFor | null>(null);
  if (open && want) {
    if (last.current?.key !== loc.key) last.current = { key: loc.key, id: want, row: target, legacy: (loc.state as { legacy?: string } | null)?.legacy };
    else if (target) last.current.row = target;
  }
  /** シートを閉じたところ（URL が一覧に変わるまで）。消した口座の URL のまま一覧が描き直されても「見つかりません」を出さない */
  const closing = useRef(false);
  const close = () => {
    closing.current = true;
    nav(LIST, { replace: true });
  };
  // 無い口座の URL（消した・別の会社の口座）：知らせて一覧へ
  useEffect(() => {
    if (!want) closing.current = false;
    else if (q.data && !closing.current && want !== "new" && !target) {
      toast(BANK_MSG.notFound);
      nav(LIST, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, want, target]);
  /** 保存・既定にした・削除した：一覧を差し替えて、シートを閉じてトースト */
  const onSaved = (p: BankAccountsSaved) => {
    qc.setQueryData(BANK_ACCOUNTS_KEY, { accounts: p.accounts, legacyBankText: p.legacyBankText });
    void qc.invalidateQueries({ queryKey: ["company"] });
    void qc.invalidateQueries({ queryKey: ["invoice"] });
    toast(p.message);
    close();
  };

  const head = (end: ReactNode) => (
    <>
      {pane.twoCol ? null : (
        <div className="cotop">
          <BackButton back={back} />
          <span className="cotop-sp" />
          {end}
        </div>
      )}
      <div className="ptitle-row">
        <h1 className="ptitle">{BANK_MSG.title}</h1>
        {pane.twoCol ? end : null}
      </div>
    </>
  );
  const cls = "ppage bankpage" + (pane.twoCol ? " is-pane" : "");

  if (me.data && !admin) {
    return (
      <div className={cls} data-testid="bank-accounts">
        {head(null)}
        <p className="readonly-note">{BANK_MSG.adminOnly}</p>
      </div>
    );
  }
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  const { accounts, legacyBankText } = q.data;
  const def = accounts.find((a) => a.isDefault) ?? accounts[0];
  const sheet = last.current;

  return (
    <div className={cls} data-testid="bank-accounts">
      {head(
        <Link className="btn text cotop-btn" id="addBankAccount" to={`${LIST}/new`} replace aria-label={BANK_MSG.addTitle} title={BANK_MSG.addTitle}>
          {BANK_MSG.add}
        </Link>,
      )}
      {accounts.length === 0 ? (
        <div className="bank-empty" id="noBankAccounts" data-testid="bank-empty">
          <RowIcon k="bank" size={56} />
          <p className="bank-empty-ttl">{BANK_MSG.noneTitle}</p>
          <p className="bank-empty-sub">{BANK_MSG.noneSub}</p>
          <Link className="btn primary bank-empty-add" id="addFirstBankAccount" to={`${LIST}/new`} replace>
            {BANK_MSG.addTitle}
          </Link>
        </div>
      ) : (
        <>
          <GroupSection note={BANK_MSG.listNote} data-testid="bank-sec-list">
            <GroupList id="bankAccountList">
              {accounts.map((a) => (
                <BankAccountRow key={a.id} title={`${a.bankName} ${a.branchName}`.trim()} sub={subOf(a)} isDefault={a.isDefault} defaultLabel="既定" to={`${LIST}/${a.id}`} data-account={a.id} data-default={a.isDefault ? "1" : "0"} />
              ))}
            </GroupList>
          </GroupSection>
          <GroupSection
            title={BANK_MSG.sectionPrint}
            help={<Help testId="help-bank-accounts" text="請求書に印字する振込先です。口座を2つ以上登録すると、請求書ごとに選べます（既定の口座が最初に入ります）。口座番号と名義は暗号化して保存します。出したあとの請求書の振込先は、口座を修正しても変わりません。" />}
            data-testid="bank-sec-print"
          >
            <GroupList plain>
              <GroupTextRow id="bankAccountText">{bankAccountText(def)}</GroupTextRow>
            </GroupList>
          </GroupSection>
        </>
      )}
      {legacyBankText ? (
        <GroupSection id="legacyBank" title={BANK_MSG.sectionLegacy} note={BANK_MSG.legacyNote} data-testid="bank-sec-legacy">
          <GroupList plain>
            <GroupTextRow testId="legacy-text">{legacyBankText}</GroupTextRow>
            <GroupNavRow icon={<RowIcon k="bank" />} title={BANK_MSG.legacyAction} buttonId="splitLegacyBank" onClick={() => nav(`${LIST}/new`, { replace: true, state: { legacy: legacyBankText } })} />
          </GroupList>
        </GroupSection>
      ) : null}
      {sheet ? <BankAccountSheet key={sheet.key} open={open} target={sheet.id === "new" ? undefined : sheet.row} accounts={accounts} legacy={sheet.id === "new" ? sheet.legacy : undefined} onClose={close} onSaved={onSaved} /> : null}
      {node}
    </div>
  );
}
